import { createStyles, makeStyles } from '@mui/styles';
import { BooleanField, ImageField, TextField, WithRecord } from 'react-admin';

import { GetInstrumentOneResponse } from '@Common/types/bo/instruments';

import { ShowFields, ShowSection } from '@sh4res/bo-common/components';
import { CcbSection } from '@sh4res/bo-common/components/Instruments/sections/Details/components/CcbSection';
import { CryptoSection } from '@sh4res/bo-common/components/Instruments/sections/Details/components/CryptoSection';
import { MutualFundSection } from '@sh4res/bo-common/components/Instruments/sections/Details/components/MutualFundSection';
import { StockSection } from '@sh4res/bo-common/components/Instruments/sections/Details/components/StockSection';
import { StructuredProductSection } from '@sh4res/bo-common/components/Instruments/sections/Details/components/StructuredProductSection';
import { statusChoices } from '@sh4res/bo-common/components/Instruments/status/choices';
import { IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

export const InstrumentDetailsSection = () => {
  const classes = useStyles();
  const isExternalBo = isBoExternal();

  return (
    <>
      <ShowSection label="general.sections.details">
        <ShowFields<GetInstrumentOneResponse>
          fields={{
            id: (
              <IdField
                reference="instruments"
                data-testid="BoExternalInstrumentDetailsSection_IdField"
              />
            ),
            symbol: <TextField data-testid="BoExternalInstrumentDetailsSection_SymbolField" />,
            name: <TextField data-testid="BoExternalInstrumentDetailsSection_NameField" />,
            about: <TextField data-testid="BoExternalInstrumentDetailsSection_AboutField" />,
            short_name: (
              <TextField data-testid="BoExternalInstrumentDetailsSection_ShortNameField" />
            ),
            short_description: (
              <TextField data-testid="BoExternalInstrumentDetailsSection_ShortDescriptionField" />
            ),
            is_pea_eligible: isExternalBo ? undefined : (
              <BooleanField data-testid="BoExternalInstrumentDetailsSection_IsPeaEligibleField" />
            ),
            logo_path: (
              <ImageField
                className={classes.image}
                data-testid="BoExternalInstrumentDetailsSection_LogoPathField"
              />
            ),
            exchange: isExternalBo ? undefined : (
              <TextField data-testid="BoExternalInstrumentDetailsSection_ExchangeField" />
            ),
            country_code: (
              <TextField data-testid="BoExternalInstrumentDetailsSection_CountryCodeField" />
            ),
            legal_status: (
              <TextField data-testid="BoExternalInstrumentDetailsSection_LegalStatusField" />
            ),
            class: <TextField data-testid="BoExternalInstrumentDetailsSection_ClassField" />,
            isin: (
              <TextField source="" data-testid="BoExternalInstrumentDetailsSection_IsinField" />
            ),
            settlement_location: isExternalBo ? undefined : (
              <TextField
                source=""
                data-testid="BoExternalInstrumentDetailsSection_SettlementLocationField"
              />
            ),
            sub_custodian_account_number: isExternalBo ? undefined : (
              <TextField
                source=""
                data-testid="BoExternalInstrumentDetailsSection_SubCustodianAccountNumberField"
              />
            ),
            status: isExternalBo ? undefined : (
              <SelectChipField
                choices={statusChoices}
                data-testid="BoExternalInstrumentDetailsSection_StatusField"
              />
            ),
            is_complex: isExternalBo ? undefined : (
              <BooleanField data-testid="BoExternalInstrumentDetailsSection_IsComplexField" />
            ),
            is_ftt_rate_zero_override: isExternalBo ? undefined : (
              <BooleanField data-testid="BoExternalInstrumentDetailsSection_IsFttRateZeroOverrideField" />
            ),
            intermediate_instrument_parent_id: isExternalBo ? undefined : (
              <IdField
                reference="instruments"
                data-testid="BoExternalInstrumentDetailsSection_IntermediateInstrumentParentIdField"
              />
            ),
            created_at: (
              <UTCDateField
                showTime={!isBoExternal()}
                data-testid="BoExternalInstrumentDetailsSection_CreatedAtField"
              />
            ),
            marketing_date_start_at: isExternalBo ? undefined : (
              <UTCDateField data-testid="BoExternalInstrumentDetailsSection_MarketingDateStartAtField" />
            ),
            marketing_date_end_at: isExternalBo ? undefined : (
              <UTCDateField data-testid="BoExternalInstrumentDetailsSection_MarketingDateEndAtField" />
            ),
            maturity_date: isExternalBo ? undefined : (
              <UTCDateField data-testid="BoExternalInstrumentDetailsSection_MaturityDateField" />
            ),
            custom_execution_flow: isExternalBo ? undefined : (
              <TextField data-testid="BoExternalInstrumentDetailsSection_CustomExecutionFlowField" />
            ),
          }}
        />
      </ShowSection>
      <WithRecord
        render={(instrument: GetInstrumentOneResponse) => (
          <>
            {['stock-us', 'stock-uv-eu'].includes(instrument.type) && <StockSection />}
            {instrument.type === 'crypto' && <CryptoSection />}
            {instrument.type === 'mutual-fund' &&
              instrument.asset_type !== 'structured-product' && (
                <MutualFundSection isExternalBo={isExternalBo} />
              )}
            {instrument.type === 'mutual-fund' &&
              instrument.asset_type === 'structured-product' && <StructuredProductSection />}
            {instrument.type === 'ccb' && <CcbSection isExternalBo={isExternalBo} />}
          </>
        )}
      />
    </>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    image: {
      '& img': {
        width: 70,
        height: 70,
        margin: 0,
      },
    },
  }),
);
